import { memo, useEffect, useMemo, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  MiAvatarCard,
  MiInputSelectBox,
  MiModalConfirm,
} from "@miplatform/components";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useAnchor } from "@miplatform/hooks";
import { getDocumentUrl } from "@miplatform/utils";
import * as mdi from "@mdi/js";
import { Divider, Menu, Typography } from "@mui/material";
import { Box } from "@mui/system";
import {
  ISSUE_RESULT_NOTE_TYPES,
  getIssueUsers,
  getNoteHistoryState,
} from "./issuesUtil";
import { useIssueBillableViewModel } from "./useIssueBillableViewModel";

export const IssueUsersHeader = memo(
  ({ title, attempt, issue = {}, users = [], showBillableToggle = false }) => {
    const [pendingBillable, setPendingBillable] = useState(null);
    const [anchorEl, handleOpen, handleClose] = useAnchor();

    const {
      getIssue,
      updateBillable,
      issue: fetchedIssue,
      isLoading,
    } = useIssueBillableViewModel();
    const issueUsers = useMemo(
      () => getIssueUsers(attempt, issue, users),
      [attempt, issue, users]
    );

    const isMultipleUsers = issueUsers.length > 1;
    const currentIssue = useMemo(() => {
      if (fetchedIssue?.id !== issue.id) return issue;

      const fetchedIssueResult =
        fetchedIssue?.issueResult ||
        fetchedIssue?.issueResults?.find(
          (issueResult) => issueResult.attemptId === attempt.id
        );

      if (!fetchedIssueResult) return issue;

      return {
        ...issue,
        issueResult: {
          ...issue.issueResult,
          ...fetchedIssueResult,
          noteHistory:
            fetchedIssueResult.noteHistory ??
            issue.issueResult?.noteHistory ??
            [],
        },
      };
    }, [attempt.id, fetchedIssue, issue]);
    const isBillable = Boolean(
      currentIssue.issueResult?.isBillable ?? currentIssue.isBillable
    );
    const billableState = getNoteHistoryState({
      noteHistory: currentIssue.issueResult?.noteHistory || [],
      noteType: ISSUE_RESULT_NOTE_TYPES.IS_BILLABLE,
      assignedUserId: attempt.assignedUserId,
    });
    const { classes, theme } = useStyles({
      fieldState: billableState.state,
    });

    useEffect(() => {
      if (showBillableToggle && issue.id) {
        getIssue({ id: issue.id });
      }
    }, [issue.id, showBillableToggle]);

    const handleBillableChange = (event) => {
      const nextBillable = Number(event.target.value) === 1;

      if (nextBillable !== isBillable) {
        setPendingBillable(nextBillable);
      }
    };

    const closeBillableModal = () => setPendingBillable(null);

    const confirmBillableChange = async () => {
      if (pendingBillable === null || !currentIssue.id) return;

      const response = await updateBillable({
        currentIssue,
        attempt,
        isBillable: pendingBillable,
      });

      if (response?.status === HTTP_STATUSES.OK) {
        closeBillableModal();
        getIssue({ id: issue.id });
      }
    };

    return (
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap={2} alignItems="center" flex={1}>
          <Box display="flex" alignItems="center">
            <Typography variant="body1" fontWeight="bold" maxWidth={420}>
              {title}
            </Typography>
          </Box>
          {showBillableToggle && (
            <Box maxWidth={100}>
              <MiInputSelectBox
                name="billable"
                className={classes.inputSelectBox}
                disableNone
                disabled={isLoading || pendingBillable !== null}
                value={isBillable ? 1 : 0}
                handleChange={handleBillableChange}
                options={[
                  { label: "Billable", value: 1 },
                  { label: "Non-Billable", value: 0 },
                ]}
              />
            </Box>
          )}
        </Box>
        <Box display="flex" gap={2} alignItems="center">
          {issue.areaName && (
            <Typography className={classes.text} variant="body2">
              {issue.areaName}
            </Typography>
          )}
          {issue.time && (
            <>
              <Divider orientation="vertical" variant="middle" flexItem />
              <Typography className={classes.text} variant="body2">
                {issue.time}
              </Typography>
            </>
          )}
          <Divider orientation="vertical" variant="middle" flexItem />
          <MiAvatarCard
            text={issueUsers.length > 0 ? issueUsers[0]?.name : "Unassigned"}
            testId="issue-main-user"
            src={getDocumentUrl(issueUsers[0]?.profilePicture?.id)}
            small
            isDropDown={isMultipleUsers}
            onClick={handleOpen}
          />
          <Menu
            anchorEl={anchorEl}
            open={anchorEl && isMultipleUsers}
            onClose={handleClose}
            classes={{
              list: classes.menu,
            }}
          >
            {issueUsers.slice(1).map((x, index) => (
              <MiAvatarCard
                testId={`issue-user-${index}`}
                key={x.userId}
                text={x?.name}
                src={getDocumentUrl(x.profilePicture?.id)}
                small
              />
            ))}
          </Menu>
        </Box>
        <MiModalConfirm
          open={pendingBillable !== null}
          title="Change Billable Status"
          titleIcon={{
            path: pendingBillable
              ? mdi.mdiCheckCircleOutline
              : mdi.mdiAlertOutline,
            color: pendingBillable
              ? theme.palette.success.main
              : theme.palette.warning.main,
          }}
          description={`Are you sure you want to mark this issue as ${
            pendingBillable ? "billable" : "non-billable"
          }?`}
          handleNegative={closeBillableModal}
          handlePositive={confirmBillableChange}
          loading={isLoading}
        />
      </Box>
    );
  }
);

const useStyles = makeStyles()((theme, { fieldState }) => {
  const stateColors = {
    warning: {
      color: theme.palette.warning.main,
      borderColor: theme.palette.warning.main,
      backgroundColor: theme.palette.warning.light,
    },
    success: {
      color: theme.palette.success.main,
      borderColor: theme.palette.success.main,
      backgroundColor: theme.palette.success.light,
    },
    default: {
      color: theme.palette.grey.medium,
      borderColor: theme.palette.grey.darkAccent,
      backgroundColor: theme.palette.grey.lightAccent,
    },
  };
  const colors = stateColors[fieldState] || stateColors.default;

  return {
    text: {
      color: theme.palette.grey.medium,
      fontWeight: theme.typography.fontWeightBold,
      lineHeight: 2,
    },
    menu: {
      paddingBottom: 0,
      paddingTop: 0,
    },
    inputSelectBox: {
      maxWidth: 100,
      "& .MuiSelect-select": {
        padding: theme.spacing(0, 2),
        display: "flex",
        alignItems: "center",
        borderRadius: theme.spacing(0.5),
        minWidth: 20,
        gap: theme.spacing(0.5),
        fontWeight: theme.fontWeight.semiBold,
        fontSize: 12,
        height: 24,
        color: colors.color,
      },

      "& .MuiOutlinedInput-notchedOutline": {
        borderColor: colors.borderColor,
      },

      "& .MuiInputBase-root": {
        borderRadius: theme.spacing(0.5),
        width: 110,
        backgroundColor: colors.backgroundColor,
      },
      "& .MuiOutlinedInput-root": {
        "& fieldset": {},
        "&:hover fieldset": {},
        "&.Mui-focused fieldset": {},
      },
    },
  };
});
